import React, { useEffect, useState, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { format } from 'date-fns';
import api from '../api';

const TEAL = '#4fc3c3';
const NAVY = '#2d3748';
const STAGE_COLORS = {
  sent: '#a0aec0', followed_up: '#4fc3c3', engaged: '#3182ce',
  won: '#38a169', cold: '#718096', do_not_contact: '#e53e3e', bounced: '#dd6b20',
};
const NAV = [
  ['overview', 'Pipeline Overview'],
  ['outlines', 'Outline A/B'],
  ['stages', 'Lead Stages'],
  ['trends', 'Trends'],
  ['bookings', 'Bookings'],
  ['activity', 'Recent Activity'],
];

// ---------- small reusable bits ----------
const Card = ({ children, style }) => (
  <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: 20, ...style }}>
    {children}
  </div>
);

const Metric = ({ label, value, accent }) => (
  <Card>
    <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 30, fontWeight: 800, color: accent || NAVY, marginTop: 6 }}>{value}</div>
  </Card>
);

const useData = (endpoint) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    api.get(endpoint)
      .then((r) => live && setData(r.data))
      .catch((e) => live && setErr(e.response?.data?.error || e.message));
    return () => { live = false; };
  }, [endpoint]);
  return [data, err];
};

const Loading = () => <div style={{ color: 'var(--muted)', padding: 20 }}>Loading…</div>;
const ErrBox = ({ msg }) => (
  <div style={{ background: '#fff5f5', color: '#c53030', padding: 14, borderRadius: 8, fontSize: 13 }}>
    Error: {msg}
  </div>
);

// ---------- sections ----------
function Overview() {
  const [d, err] = useData('/overview');
  if (err) return <ErrBox msg={err} />;
  if (!d) return <Loading />;
  const cards = [
    ['Total Leads', d.total_leads], ['Emails Sent', d.total_sent],
    ['Opened', `${d.total_opened} (${d.open_rate}%)`],
    ['Clicked', `${d.total_clicked} (${d.click_rate}%)`],
    ['Booked', `${d.total_booked} (${d.booking_rate}%)`],
    ['Engaged', d.total_engaged], ['Won', d.total_won, '#38a169'],
    ['Bounced', d.total_bounced, '#dd6b20'],
  ];
  return (
    <div style={grid(4)}>
      {cards.map(([l, v, a]) => <Metric key={l} label={l} value={v} accent={a} />)}
    </div>
  );
}

function OutlinePerformance() {
  const [d, err] = useData('/outlines');
  if (err) return <ErrBox msg={err} />;
  if (!d) return <Loading />;
  const chart = d.map((o) => ({ name: `Outline ${o.outline}`, 'Click %': o.click_rate, 'Open %': o.open_rate }));
  return (
    <>
      <div style={grid(d.length || 1)}>
        {d.map((o) => (
          <Card key={o.outline}>
            <div style={{ fontWeight: 700, color: NAVY }}>Outline {o.outline}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8, lineHeight: 1.8 }}>
              Sent: <b>{o.sent}</b><br />
              Opened: <b>{o.opened}</b> ({o.open_rate}%)<br />
              Clicked: <b>{o.clicked}</b> ({o.click_rate}%)
            </div>
          </Card>
        ))}
      </div>
      <Card style={{ marginTop: 16 }}>
        <h4 style={h4}>Engagement by outline</h4>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="Open %" fill={NAVY} radius={[4, 4, 0, 0]} />
            <Bar dataKey="Click %" fill={TEAL} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </>
  );
}

function LeadStages() {
  const [d, err] = useData('/stages');
  if (err) return <ErrBox msg={err} />;
  if (!d) return <Loading />;
  const pie = d.stages.map((s) => ({ name: s.stage, value: s.count }));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <Card>
        <h4 style={h4}>Stage distribution</h4>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
              {pie.map((p) => <Cell key={p.name} fill={STAGE_COLORS[p.name] || '#cbd5e0'} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </Card>
      <Card>
        <h4 style={h4}>Tier performance</h4>
        <table>
          <thead>
            <tr style={trHead}>
              <th style={th}>Tier</th><th style={th}>Sent</th>
              <th style={th}>Clicked</th><th style={th}>Click %</th>
            </tr>
          </thead>
          <tbody>
            {d.tiers.map((t) => (
              <tr key={t.tier} style={trBody}>
                <td style={td}>Tier {t.tier}</td>
                <td style={td}>{t.sent}</td>
                <td style={td}>{t.clicked}</td>
                <td style={{ ...td, fontWeight: 700, color: TEAL }}>{t.click_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Trends() {
  const [d, err] = useData('/trends');
  if (err) return <ErrBox msg={err} />;
  if (!d) return <Loading />;
  return (
    <>
      <Card>
        <h4 style={h4}>Daily sends — last 14 days</h4>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={d.daily}>
            <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Line type="monotone" dataKey="sent" stroke={TEAL} strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>
      <Card style={{ marginTop: 16 }}>
        <h4 style={h4}>Weekly sends — last 8 weeks</h4>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={d.weekly}>
            <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="sent" fill={NAVY} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </>
  );
}

const fmt = (v) => { try { return format(new Date(v), 'MMM d, HH:mm'); } catch { return v || '—'; } };

function Bookings() {
  const [d, err] = useData('/bookings');
  if (err) return <ErrBox msg={err} />;
  if (!d) return <Loading />;
  return (
    <Card>
      <h4 style={h4}>Confirmed bookings</h4>
      <table>
        <thead>
          <tr style={trHead}>
            {['Name', 'Email', 'Phone', 'Specialty', 'Company', 'When'].map((c) => <th key={c} style={th}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {d.length === 0 && <tr><td style={td} colSpan={6}>No bookings yet.</td></tr>}
          {d.map((b, i) => (
            <tr key={i} style={trBody}>
              <td style={td}>{b.name}</td><td style={td}>{b.email}</td>
              <td style={td}>{b.phone}</td><td style={td}>{b.specialty}</td>
              <td style={td}>{b.company}</td><td style={td}>{fmt(b.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Activity() {
  const [d, err] = useData('/activity');
  if (err) return <ErrBox msg={err} />;
  if (!d) return <Loading />;
  return (
    <Card>
      <h4 style={h4}>Last 50 leads touched</h4>
      <table>
        <thead>
          <tr style={trHead}>
            {['Company', 'Email', 'Specialty', 'Tier', 'Outline', 'Stage', 'Sent'].map((c) => <th key={c} style={th}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {d.map((r, i) => (
            <tr key={i} style={trBody}>
              <td style={td}>{r.company}</td><td style={td}>{r.email}</td>
              <td style={td}>{r.specialty}</td><td style={td}>{r.tier}</td>
              <td style={td}>{r.outline}</td>
              <td style={td}><span style={badge(r.stage)}>{r.stage}</span></td>
              <td style={td}>{fmt(r.sent_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ---------- shell ----------
export default function Dashboard({ onLogout }) {
  const [active, setActive] = useState('overview');
  const [refresh, setRefresh] = useState(Date.now());
  const title = NAV.find(([k]) => k === active)[1];
  const bump = useCallback(() => setRefresh(Date.now()), []);

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <aside style={sb.aside}>
        <div style={sb.brand}>Med<span style={{ color: TEAL }}>Copy</span>USA</div>
        <nav style={{ marginTop: 24 }}>
          {NAV.map(([k, label]) => (
            <button key={k} onClick={() => setActive(k)}
              style={{ ...sb.item, ...(active === k ? sb.itemActive : {}) }}>
              {label}
            </button>
          ))}
        </nav>
        <div style={sb.foot}>Houston, Texas<br />ai.medcopyusa.com</div>
      </aside>

      <main style={{ flex: 1, padding: 28, maxWidth: 1200 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <h2 style={{ margin: 0, color: NAVY }}>{title}</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={btn.ghost} onClick={bump}>Refresh</button>
            <button style={btn.danger} onClick={onLogout}>Sign out</button>
          </div>
        </div>
        <div key={`${active}-${refresh}`}>
          {active === 'overview' && <Overview />}
          {active === 'outlines' && <OutlinePerformance />}
          {active === 'stages' && <LeadStages />}
          {active === 'trends' && <Trends />}
          {active === 'bookings' && <Bookings />}
          {active === 'activity' && <Activity />}
        </div>
      </main>
    </div>
  );
}

// ---------- style helpers ----------
const grid = (n) => ({ display: 'grid', gridTemplateColumns: `repeat(${Math.min(n, 4)}, 1fr)`, gap: 16 });
const h4 = { margin: '0 0 14px', color: NAVY, fontSize: 15 };
const trHead = { borderBottom: '2px solid var(--border)' };
const trBody = { borderBottom: '1px solid var(--border)' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase' };
const td = { padding: '10px 12px', fontSize: 13 };
const badge = (stage) => ({
  background: (STAGE_COLORS[stage] || '#cbd5e0') + '22',
  color: STAGE_COLORS[stage] || '#4a5568',
  padding: '3px 9px', borderRadius: 12, fontSize: 12, fontWeight: 600,
});
const sb = {
  aside: { width: 230, background: NAVY, color: '#fff', padding: '26px 18px', display: 'flex', flexDirection: 'column' },
  brand: { fontSize: 20, fontWeight: 800 },
  item: { display: 'block', width: '100%', textAlign: 'left', background: 'transparent', color: 'rgba(255,255,255,0.7)', border: 'none', padding: '11px 14px', borderRadius: 8, fontSize: 14, marginBottom: 4 },
  itemActive: { background: 'rgba(79,195,195,0.18)', color: TEAL, fontWeight: 600 },
  foot: { marginTop: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 },
};
const btn = {
  ghost: { background: '#fff', border: '1px solid var(--border)', padding: '8px 14px', borderRadius: 8, fontSize: 13, color: NAVY },
  danger: { background: '#fff', border: '1px solid #fed7d7', padding: '8px 14px', borderRadius: 8, fontSize: 13, color: '#c53030' },
};
